import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useRegisterUser } from "pages/Dashboard/dashboardQueries";

const RegisterFormSchema = yup.object().shape({
  name: yup
    .string()
    .required("Full Name is a required field")
    .min(2, "Name must be at least 2 characters"),
  email: yup
    .string()
    .email("Must be a valid email")
    .when("phone", {
      is: (phone) => !phone || phone.trim() === "",
      then: (schema) => schema.required("Either Email or Phone Number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  phone: yup
    .string()
    .when("email", {
      is: (email) => !email || email.trim() === "",
      then: (schema) => schema.required("Either Email or Phone Number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  password: yup
    .string()
    .required("Password is a required field")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
      "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
    ),
  role: yup
    .string()
    .oneOf(["SUPER_ADMIN", "ADMIN", "USER", "ENUMERATOR"], "Invalid Role")
    .required("Role is a required field"),
}, [["email", "phone"]]);

const UserRegistrationForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const { mutate, isLoading } = useRegisterUser();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(RegisterFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      role: "ADMIN",
    },
  });

  const onSubmit = (data) => {
    setApiErrorMessage("");
    // Clean up empty fields before sending to API
    const requestData = {
      name: data.name,
      password: data.password,
      role: data.role,
    };
    if (data.email && data.email.trim() !== "") {
      requestData.email = data.email.trim();
    }
    if (data.phone && data.phone.trim() !== "") {
      requestData.phone = data.phone.trim();
    }

    mutate(requestData, {
      onSuccess: () => {
        reset();
      },
      onError: (error) => {
        const errMsg =
          error?.data?.error ||
          error?.data?.message ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        
        if (Array.isArray(errMsg)) {
          setApiErrorMessage(errMsg.map((e) => e.message).join(", "));
        } else if (typeof errMsg === "object" && errMsg.message) {
          setApiErrorMessage(errMsg.message);
        } else {
          setApiErrorMessage(String(errMsg));
        }
      },
    });
  };

  return (
    <Box className="flex justify-center items-center w-full py-10">
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "550px",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #4A4D9A 0%, #3D3E78 100%)",
            px: 4,
            py: 3.5,
            color: "#ffffff",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "-0.5px" }}>
            Add New User
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
            Create a user account with specific roles and credentials.
          </Typography>
        </Box>

        {/* Form Body */}
        <Box sx={{ p: 4 }}>
          {apiErrorMessage && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#FEF2F2",
                border: "1px solid #FCA5A5",
                color: "#991B1B",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {apiErrorMessage}
              </Typography>
            </Box>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={3}>
              <Box>
                <label className="block text-[#1B2559] text-sm font-bold mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter full name"
                  variant="outlined"
                  {...register("name")}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                />
              </Box>

              <Box>
                <label className="block text-[#1B2559] text-sm font-bold mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <FormControl fullWidth>
                  <Select
                    {...register("role")}
                    error={Boolean(errors.role)}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#F8FAFC",
                    }}
                  >
                    <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                    <MenuItem value="ADMIN">Admin</MenuItem>
                    <MenuItem value="ENUMERATOR">Enumerator</MenuItem>
                    <MenuItem value="USER">User (Worker)</MenuItem>
                  </Select>
                  {errors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box>
                <label className="block text-[#1B2559] text-sm font-bold mb-2">
                  Email Address
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter email address"
                  variant="outlined"
                  type="email"
                  {...register("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                />
              </Box>

              <Box>
                <label className="block text-[#1B2559] text-sm font-bold mb-2">
                  Phone Number
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter phone number"
                  variant="outlined"
                  {...register("phone")}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1, pl: 0.5 }}
                >
                  * Provide either an Email Address or a Phone Number to register the user.
                </Typography>
              </Box>

              <Box>
                <label className="block text-[#1B2559] text-sm font-bold mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter secure password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1, pl: 0.5, lineHeight: 1.4 }}
                >
                  Password must be at least 8 characters, and contain at least one uppercase letter, one lowercase letter, one number, and one special character (e.g. @, $, !, %, *, ?, &, #).
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isLoading}
                startIcon={<PersonAddIcon />}
                sx={{
                  mt: 2,
                  py: 1.5,
                  background: "linear-gradient(135deg, #4A4D9A 0%, #3D3E78 100%)",
                  boxShadow: "0px 4px 12px rgba(61, 62, 120, 0.25)",
                  color: "#ffffff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "16px",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5C5FAB 0%, #4D4E96 100%)",
                  },
                }}
              >
                {isLoading ? "Registering..." : "Register User"}
              </Button>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserRegistrationForm;
